import React, { useState, useEffect, useCallback } from "react";
import { push } from "connected-react-router";
import { reducerType } from "redux/reducers";
import { connect } from "react-redux";
import { Dispatch } from "redux";
import Container from "@material-ui/core/Container";
import CircularProgress from "@material-ui/core/CircularProgress";
import HeaderBarSub2 from "components/HeaderBarSub2";
import styles from "./styles";
import GridFullHeight from "components/GridFullHeight";
import GridFormContainer from "components/GridFormContainer";
import Loading from "components/Loading";
import { User } from "@shelter/core";
import {
  GET_USERS_REQUEST,
  LOADMORE_USERS_REQUEST,
  DELETE_USERS_REQUEST,
} from "redux/reducers/service/actionTypes";
import Alert from "components/Alert";
import Swipeout from "rc-swipeout";
import "rc-swipeout/assets/index.css";
import { isMobile } from "common/";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import { UserRole } from "common/";
import { useTranslation } from "react-i18next";

interface ManageAccessProps {
  dispatch: Dispatch;
  loading: boolean;
  users: User[];
  loadingMore: boolean;
  canLoadmore: boolean;
}

const mapStateToProps = (state: reducerType) => {
  return {
    loading: state.service.loading,
    users: state.service.users,
    loadingMore: state.service.loadingMore,
    canLoadmore: state.service.canLoadmore,
  };
};

const afterTranform = (users: User[]) => {
  return users.map((u) => {
    const isAdmin = u.roles && u.roles.includes(UserRole.Administrator);
    return {
      ...u,
      isAdmin,
    };
  });
};

const ManageAccess = React.memo((props: ManageAccessProps) => {
  const { dispatch, loading, loadingMore, canLoadmore } = props;
  const [users, setUsers] = useState([]);
  // eslint-disable-next-line
  const [isSwipe, setIsSwipe] = useState(false);
  const classes = styles();
  const translate = useTranslation().t;

  useEffect(() => {
    dispatch({
      type: GET_USERS_REQUEST,
      params: getQueryParams(),
    });
    // eslint-disable-next-line
  }, []);

  const getQueryParams = useCallback((keyword = "") => {
    return {
      sort: "createdAt",
      direction: "desc",
      limit: 30,
      skip: 0,
      filter: "roles",
      roles: "VOLUNTEER LEAD,VOLUNTEER,VOLUNTEER SENIOR",
      q: keyword,
      search: "email,name,displayName",
    };
  }, []);

  const loadmoreFunction = () => {
    dispatch({
      type: LOADMORE_USERS_REQUEST,
    });
  };

  const openUrl = (url) => {
    dispatch(push(url));
  };

  const handleWindowScroll = () => {
    const bottomOfWindow =
      window.scrollY + window.innerHeight >= document.body.scrollHeight - 500;
    if (bottomOfWindow && !loadingMore && canLoadmore && !loading) {
      loadmoreFunction();
    }

    return;
  };

  useEffect(() => {
    window.addEventListener("scroll", handleWindowScroll);
    return () => {
      window.removeEventListener("scroll", handleWindowScroll);
    };
  });

  useEffect(() => {
    setUsers(afterTranform(props.users));
  }, [props.users]);

  const [openAlert, setOpenAlert] = useState(false);
  const [selectedLine, setSelectedLine] = useState();

  const handleCloseAlert = () => {
    setOpenAlert(false);
    setSelectedLine(null);
  };

  const handleOpenAlert = (id) => {
    setOpenAlert(true);
    setSelectedLine(id);
  };

  const handleClickYes = () => {
    selectedLine && handleDelete(selectedLine);
    handleCloseAlert();
  };

  const handleDelete = (id) => {
    dispatch({
      type: DELETE_USERS_REQUEST,
      id: id,
    });
  };

  const handleSearch = useCallback((keyword: string) => {
    dispatch({
      type: GET_USERS_REQUEST,
      params: getQueryParams(keyword),
    });
  }, [dispatch, getQueryParams]);

  return (
    <GridFullHeight container>
      <GridFormContainer item xs={12} sm={12} md={12}>
        <GridFullHeight container>
          <HeaderBarSub2
            backUrl={"/"}
            openUrl={openUrl}
            name={translate("MANAGE_VOLUNTEERS")}
            searchPlaceholder={translate("PH_SEARCH_VOLUNTEERS")}
            isSearch={true}
            handleSearch={handleSearch}
          />  
          <Container className={classes.root}>
            {loading ? (
              <Loading />
            ) : (
              <>
                {!users.length && (
                  <p className={classes.noText}>
                    {translate("NO_VOLUNTEERS")}
                  </p>
                )}
                {users.length > 0 &&
                  users.map((user, index) => (
                    <div key={index} className={classes.pb15}>
                      <Swipeout
                        right={[
                          {
                            text: translate("EDIT"),
                            onPress: () =>
                              openUrl(`/users/${user.id}?volunteer=true`),
                            style: {
                              backgroundColor: "#BBB",
                              color: "white",
                              width: 85,
                            },
                            className: "custom-class-2",
                          },
                          {
                            text: translate("DELETE"),
                            onPress: () => handleOpenAlert(user.id),
                            style: {
                              backgroundColor: "#E55A5A",
                              color: "white",
                              width: 85,
                            },
                            className: "custom-class-2",
                          },
                        ]}
                        onOpen={() => setIsSwipe(true)}
                        onClose={() => setIsSwipe(false)}
                      >
                        <div className={classes.item}>
                          <div>
                            {user.displayName && (
                              <p className={classes.title}>
                                {translate("NAME")}: {user.displayName}
                              </p>
                            )}
                            {user.phone && (
                              <p className={classes.title}>
                                {translate("PHONE")}: {user.phone}
                              </p>
                            )}
                            {user.email && (
                              <p className={classes.title}>
                                {translate("EMAIL")}: {user.email}
                              </p>
                            )}
                            <p className={classes.title}>
                              {translate("ROLE")}:{" "}
                              {user.roles.includes("ADMINISTRATOR")
                                ? "ADMINISTRATOR"
                                : user.roles.includes("SUPER USER")
                                ? "SUPER USER"
                                : user.roles.includes("AUTO USER")
                                ? "AUTO USER"
                                : user.roles.includes("VOLUNTEER LEAD")
                                ? "VOLUNTEER LEAD"
                                : user.roles.includes("VOLUNTEER SENIOR")
                                ? "VOLUNTEER SENIOR"
                                : user.roles.includes("VOLUNTEER")
                                ? "VOLUNTEER"
                                : translate("USER")}
                            </p>
                            <p className={classes.title}>
                              {translate("ACTIVE")}:{" "}
                              {user.isApproved === undefined || user.isApproved
                                ? translate("YES")
                                : translate("NO")}
                            </p>
                            {user.lastMethod && (
                              <p className={classes.title}>
                                {translate("LAST_LOGIN_METHOD")}: {user.lastMethod}
                              </p>
                            )}
                          </div>
                          {!isMobile && (
                            <p className={classes.groupIcon}>
                              <span
                                className={classes.wrapDelete}
                                onClick={() =>
                                  openUrl(`/users/${user.id}?volunteer=true`)
                                }
                              >
                                <EditIcon className={classes.iconDelete} />
                              </span>
                              <span
                                className={classes.wrapDelete}
                                onClick={() => handleOpenAlert(user.id)}
                              >
                                <DeleteOutlineIcon
                                  className={classes.iconDelete}
                                />
                              </span>
                            </p>
                          )}
                        </div>
                      </Swipeout>
                    </div>
                  ))}
                {canLoadmore && (
                  <Container className={classes.textCenter}>
                    <CircularProgress
                      style={{ width: 25, height: 25 }}
                      className={classes.loadmore}
                    />
                  </Container>
                )}
                <Alert
                  title={translate("DELETE_USER_CONFIRM")}
                  open={openAlert}
                  handleClose={handleCloseAlert}
                  handleClickYes={handleClickYes}
                />
              </>
            )}
          </Container>
        </GridFullHeight>
      </GridFormContainer>
    </GridFullHeight>
  );
});

export default connect(mapStateToProps)(ManageAccess);
